import React from 'react'
import { Box, IconButton, Tooltip, Fade, Grow } from '@mui/material'
import Icon from 'atoms/Icon'
import { useMessagesDashboardViewContext } from '../MessagesDashboardViewContext'
import MessageActionSelector from './MessageActionSelector'

interface BulkMessageActionSelectorProps {
  actions?: Array<{
    name: string
    key: string
  }>
  onActionChange?: (action: { name: string; key: string } | null) => void
  onClose?: () => void
}

export default function BulkMessageActionSelector({
  actions,
  onActionChange,
  onClose,
}: BulkMessageActionSelectorProps) {
  const context = useMessagesDashboardViewContext()

  const executeMessageAction = () => {
    const action = context.messagesActionWapper.action
    const messageArgs = action?.definition.args

    if (messageArgs?.length) {
      // Handle action args modal if needed
      console.log('Action requires args:', messageArgs)
    } else {
      context.executeMessageAction()
    }
  }

  const handleClearSelection = () => {
    context.selectMessages([])
    // Also clear the selected action when clearing messages
    context.selectMessageAction(null)
    onActionChange?.(null)
  }

  // Determine if we can execute an action (detail message or bulk selection)
  const hasDetailMessage = !!context.messagesActionWapper.detailMessage
  const hasBulkSelection = context.messagesActionWapper.selectedMessages.length > 0
  const canExecuteAction = hasDetailMessage || hasBulkSelection

  // Only show if we can execute an action (detail message or bulk selection)
  if (!canExecuteAction) {
    return null
  }

  const getPlaceholder = () => {
    return 'Choose action...'
  }

  // Render as floating container with Fade and Grow effects like GlobalSearchModal
  return (
    <Fade
      in={canExecuteAction}
      mountOnEnter
      unmountOnExit
      easing={{
        enter: 'ease-in',
        exit: 'ease-out',
      }}
      timeout={300}
    >
      <Box
        sx={{
          position: 'fixed',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
          backgroundColor: 'white',
          borderRadius: 2,
          boxShadow: 20,
          border: '1px solid',
          borderColor: 'grey.200',
          padding: 1.5,
          minWidth: 600,
          maxWidth: '90vw',
          width: 'fit-content',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: 24,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Grow in={canExecuteAction} timeout={400}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              {hasBulkSelection ? (
                <>
                  <Icon name="check" className="text-primary" />
                  <span className="font-medium">
                    {context.messagesActionWapper.selectedMessages.length} selected
                  </span>
                </>
              ) : hasDetailMessage ? (
                <>
                  <Icon name="info" className="text-primary" />
                  <span className="font-medium text-sm">Detail Message Action</span>
                </>
              ) : null}
            </Box>
          </Grow>

          <Grow in={canExecuteAction} timeout={500}>
            <Box>
              <MessageActionSelector
                actions={actions}
                onActionChange={onActionChange}
                placeholder={'Choose action...'}
                minWidth={400}
              />
            </Box>
          </Grow>

          <Grow in={canExecuteAction} timeout={600}>
            <Tooltip title="Execute action for all selected messages">
              <IconButton
                disabled={!context.messagesActionWapper.action?.key}
                onClick={executeMessageAction}
                size="large"
                sx={{
                  color: 'white',
                  backgroundColor: 'primary.main',
                  flexShrink: 0,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                    color: 'white',
                  },
                  '&:disabled': {
                    backgroundColor: 'grey.300',
                    color: 'grey.500',
                  },
                }}
              >
                <Icon name="bolt" size={30} />
              </IconButton>
            </Tooltip>
          </Grow>

          <Grow in={canExecuteAction} timeout={700}>
            <Tooltip title="Clear selection">
              <IconButton
                onClick={handleClearSelection}
                size="large"
                sx={{
                  color: 'white',
                  backgroundColor: 'grey.600',
                  flexShrink: 0,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'grey.800',
                    color: 'white',
                  },
                }}
              >
                <Icon name="close" size={30} />
              </IconButton>
            </Tooltip>
          </Grow>
        </Box>
      </Box>
    </Fade>
  )
}
